import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { PremiumCard, dashboardTokens } from "./dashboardStyles";

const formatBRL = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
    : "R$ 0";

/**
 * Fechamentos (imóveis marcados como "vendido pela Senna" via
 * imovelInfo.statusInfo.tipo). "Volume negociado" é o valor bruto do
 * negócio (imovelInfo.statusInfo.valorVenda, ou o valor de tabela) — NÃO é
 * comissão/receita real da imobiliária, porque não existe hoje um campo de
 * comissão preenchido de forma confiável (ver relatório).
 */
function ClosingsSection({ closings, verde, cinza, periodLabel }) {
  const hasVolumeData = closings.volumeUltimos6Meses.some((m) => m.volume > 0);
  const hasWeekData = closings.porDia.some((d) => d.vendas > 0 || d.alugueis > 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <PremiumCard sx={{ p: 2.25, height: "100%" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: dashboardTokens.textSecondary }}>
            Fechamentos — {periodLabel}
          </Typography>
          <Typography sx={{ fontSize: "1.9rem", fontWeight: 700, mt: 0.75 }}>{closings.total}</Typography>
          <Typography sx={{ fontSize: "0.8rem", color: dashboardTokens.textSecondary, mt: 0.5 }}>
            {closings.vendas} venda{closings.vendas !== 1 ? "s" : ""} · {closings.alugueis} aluguel{closings.alugueis !== 1 ? "eis" : ""}
          </Typography>
          {closings.variacaoPct !== null && (
            <Typography sx={{ fontSize: "0.76rem", color: dashboardTokens.textSecondary, mt: 0.75 }}>
              {closings.variacaoPct >= 0 ? "+" : ""}
              {closings.variacaoPct.toFixed(1)}% vs período anterior
            </Typography>
          )}
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${dashboardTokens.border}` }}>
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: dashboardTokens.textSecondary }}>
              Volume negociado (bruto)
            </Typography>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, mt: 0.5 }}>{formatBRL(closings.volumeNegociado)}</Typography>
          </Box>
        </PremiumCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <PremiumCard sx={{ p: 2, height: "100%" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1 }}>Fechamentos por dia útil (semana atual)</Typography>
          {hasWeekData ? (
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={closings.porDia}>
                  <CartesianGrid vertical={false} strokeDasharray="3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={24} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="vendas" name="Vendas" fill={verde} />
                  <Bar dataKey="alugueis" name="Aluguéis" fill={cinza} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Stack sx={{ height: 200 }} alignItems="center" justifyContent="center">
              <Typography sx={{ fontSize: "0.82rem", color: dashboardTokens.textSecondary }}>
                Nenhum fechamento registrado nesta semana.
              </Typography>
            </Stack>
          )}
        </PremiumCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <PremiumCard sx={{ p: 2, height: "100%" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1 }}>Volume negociado — últimos 6 meses</Typography>
          {hasVolumeData ? (
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={closings.volumeUltimos6Meses}>
                  <CartesianGrid vertical={false} strokeDasharray="3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={32} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <RTooltip formatter={(v) => formatBRL(v)} />
                  <Line type="monotone" dataKey="volume" stroke={verde} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Stack sx={{ height: 200 }} alignItems="center" justifyContent="center">
              <Typography sx={{ fontSize: "0.82rem", color: dashboardTokens.textSecondary }}>
                Nenhum fechamento nos últimos 6 meses.
              </Typography>
            </Stack>
          )}
        </PremiumCard>
      </Grid>
    </Grid>
  );
}

export default ClosingsSection;
